import { useState, useEffect } from 'react';
import { StreamVideoClient, User } from '@stream-io/video-react-sdk';
import { STREAM_API_KEY } from '@api/constants';

interface UseStreamVideoClientReturn {
  videoClient: StreamVideoClient | null;
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useStreamVideoClient(): UseStreamVideoClientReturn {
  const [videoClient, setVideoClient] = useState<StreamVideoClient | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    let client: StreamVideoClient | null = null;
    let shouldCleanup = false;

    const initVideoClient = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Create anonymous user (viewer-only, no MAU cost)
        const anonymousUser: User = {
          type: 'anonymous',
        };

        // Create new StreamVideoClient instance with anonymous user
        client = new StreamVideoClient({
          apiKey: STREAM_API_KEY,
          user: anonymousUser,
        });
        shouldCleanup = true;

        // Update state if still mounted
        if (mounted) {
          setVideoClient(client);
          setIsLoading(false);
        } else {
          // Component unmounted during init - clean up
          await client.disconnectUser().catch(() => {
            // Ignore disconnect errors
          });
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to connect to video client');
          setIsLoading(false);
        }
      }
    };

    initVideoClient();

    return () => {
      mounted = false;
      if (shouldCleanup && client) {
        client.disconnectUser().catch(() => {
          // Ignore disconnect errors
        });
      }
    };
  }, []);

  return {
    videoClient,
    isReady: !isLoading && !!videoClient && !error,
    isLoading,
    error,
  };
}
